/**
 * Created by andreaskarantzas on 09.04.2021.
 */
import { makeAutoObservable } from 'mobx';
import { HttpBackend } from '../../Services/Http/HttpBackend';
import { v1ParamsToV3Params } from './exploreV3Params';
import { ExploreEntryQueryRequest } from './ExploreEntryQueryRequest';
import { Activity } from '../Activity/Activity';
import { SubscribableEntryBase, createSubscribableEntryBase } from './SubscribableEntry';
import { ExploreEntryUpcomingQueryRequest } from './ExploreEntryUpcomingQueryRequest';
import dayjs from 'dayjs';
import { OnlineMeetingEntryEmbedData, createOnlineMeetingEntryEmbedData } from './OnlineMeetingEntryEmbedData';

export type OnlineMeetingEntry = SubscribableEntryBase & {
  type: 'onlineMeeting';
  meetingLink: string;
  language?: string;
  activityIdentifier?: string;
  data: Record<string, any>;
  embedData?: OnlineMeetingEntryEmbedData;
  activity?: Activity;
  readonly canAlreadyJoinNextDate: boolean;
  readonly isNotVimeoLink: boolean;
};

export function createOnlineMeetingEntry(json?: Partial<OnlineMeetingEntry>): OnlineMeetingEntry {
  const base = createSubscribableEntryBase(json, 'onlineMeeting');
  return makeAutoObservable({
    ...base,
    meetingLink: json?.meetingLink ?? '',
    language: json?.language,
    activityIdentifier: json?.activityIdentifier,
    data: json?.data ?? {},
    embedData: json?.embedData ? createOnlineMeetingEntryEmbedData(json.embedData as any) : undefined,
    activity: undefined as Activity | undefined,

    get canAlreadyJoinNextDate(): boolean {
      const nextMeetingDate = this.nextDate;
      const nextMeetingEndDate = nextMeetingDate?.add(dayjs.duration(this.duration));
      return nextMeetingDate && nextMeetingEndDate
        ? !dayjs().isBefore(nextMeetingDate.subtract(60, 'minutes')) &&
            !dayjs().isAfter(nextMeetingEndDate.add(15, 'minutes'))
        : false;
    },

    get isNotVimeoLink(): boolean {
      return !this.meetingLink.startsWith('https://vimeo.com/');
    },
  });
}

export const OnlineMeetingEntryApi = {
  upcoming(request?: Omit<ExploreEntryUpcomingQueryRequest, 'type'>): Promise<OnlineMeetingEntry[]> {
    return HttpBackend.get(`/coach/explore/v3/entries/upcoming`, v1ParamsToV3Params({ ...request, type: 'onlineMeeting' })).then((res) =>
      (res ?? []).map((r: any) => createOnlineMeetingEntry(r)),
    );
  },

  find(request: Omit<ExploreEntryQueryRequest, 'type'>): Promise<OnlineMeetingEntry[]> {
    return HttpBackend.get(`/coach/explore/v3/entries`, v1ParamsToV3Params({ ...request, type: 'onlineMeeting' })).then((res) =>
      (res ?? []).map((r: any) => createOnlineMeetingEntry(r)),
    );
  },

  findOne(id: string): Promise<OnlineMeetingEntry> {
    return HttpBackend.get(`/coach/explore/v3/entries/${id}`).then((res) => createOnlineMeetingEntry(res));
  },
};
